import * as React from 'react';
import { FormLabel, RadioGroup, FormControlLabel, Radio, RadioGroupProps } from '@mui/material';
import FormControl from '@mui/material/FormControl';

/**
 * A single selectable option in the radio group.
 */
interface MRadioChoice {
    /** Value submitted when this option is selected. */
    id: string | number;
    /** Display label rendered next to the radio button. */
    name: string;
}

/**
 * Props for {@link MRadio}.
 */
interface MRadioProps extends Omit<RadioGroupProps, 'defaultValue' | 'value' | 'onChange'> {
    /** Field label rendered above the radio group. */
    label?: string;
    /** Array choices: `[{id: 'id', name: 'name'}, {...}]`. */
    choices: MRadioChoice[];
    /** Pre-selected value applied on first render. */
    defaultValue?: string | number;
    /** Controlled value. When provided, the component becomes controlled. */
    value?: string | number;
    /**
     * Called with the newly selected value.
     *
     * @param value - The selected option id.
     */
    onChange: (value: string | number) => void;
    /** Direction to display items. Defaults to `true` (row). */
    isDisplayRow?: boolean;
    /** Disables every choice in the group. Defaults to `false`. */
    disabled?: boolean;
}

/**
 * A MUI {@link RadioGroup} wrapper offering a consistent `choices` / `value` /
 * `defaultValue` / `onChange` contract shared by the other controls.
 */
const MRadio: React.FC<MRadioProps> = ({
    label, choices, defaultValue, value, onChange,
    isDisplayRow = true,
    disabled = false,
    ...props
}) => {
    const [mValue, setMValue] = React.useState<string | number>('');
    const [mDefaultValue, setMDefaultValue] = React.useState<string | number>('');

    React.useEffect(() => {
        if (defaultValue !== undefined) {
            setMDefaultValue(defaultValue);
            setMValue(defaultValue);
        }
    }, [defaultValue]);

    React.useEffect(() => {
        if (value !== undefined) setMValue(value);
    }, [value]);

    const handleChange: RadioGroupProps['onChange'] = (e) => {
        const newValue = e?.target?.value;
        setMValue(newValue);
        onChange(newValue);
    };

    return (
        <FormControl sx={{ minWidth: '100%' }} size="small">
            <FormLabel id={`${label}-radio-buttons-group-label`}>{label}</FormLabel >
            <RadioGroup
                {...props}
                aria-labelledby={`${label}-radio-buttons-group-label`}
                defaultValue={mDefaultValue}
                value={mValue}
                name={`${label}-radio-buttons-group`}
                row={isDisplayRow}
                onChange={handleChange}
            >
                {choices.map((m, index) => {
                    return <FormControlLabel key={index} id={String(m.id)} value={m.id} control={<Radio disabled={disabled} />} label={m.name} disabled={disabled} />;
                })}
            </RadioGroup>
        </FormControl>
    );
};

export default React.memo(MRadio);
